import { getScheduleData } from '@dispatch/shared/services';
import {
  EVENT_TYPE,
  SuperApiCalendar,
  SuperApiResource,
  SuperApiResponse,
  SuperApiTask,
  TaskListFilterGroup,
  TaskPeril,
} from '@dispatch/shared/types';
import { usePaginatedQuery } from '../api/usePaginatedQuery';
import { z } from 'zod';
import { differenceInMinutes } from 'date-fns';

type Task = z.infer<typeof SuperApiTask>;
type Calendar = z.infer<typeof SuperApiCalendar>;
type Resource = z.infer<typeof SuperApiResource>;

function mapEventName(event: Task): string {
  try {
    if (event.type === 'task') {
      return event?.insuredName?.trim() + ' - ' + event?.claimNumber?.trim();
    }
    return event.name;
  } catch (e) {
    return '-';
  }
}

const taskColors = {
  green: '#88BBA1',
  grey: '#4e4e4fff',
  blue: 'blue',
  orange: '#FF9900',
};
export const mapColor = (
  event: Task,
  perilData: { [code: string]: TaskPeril }
) => {
  const defaultPerilColor = taskColors.green;
  // we want to prioritize the color of the virtual over the peril color
  if (event?.methodOfInspection === 'VIRTUAL') {
    return taskColors.orange;
  }

  if (event?.peril !== undefined && perilData[event.peril] !== undefined) {
    return perilData[event.peril]?.color || defaultPerilColor;
  }

  if (event?.type === 'task') {
    return taskColors.blue;
  }

  return taskColors.grey;
};

function mapIcons(event: Task, perilData: { [code: string]: TaskPeril }) {
  if (event.type === 'CLAIM_OWNER') return '';
  if (
    event?.peril !== undefined &&
    perilData[event.peril] !== undefined &&
    perilData[event.peril].iconClassName !== undefined
  ) {
    return perilData[event.peril].iconClassName;
  }
  if (event?.peril) {
    return 'b-fa b-fa-house-crack';
  }
  return 'b-fa b-fa-calendar';
}

const mapEvents = (
  events: Task[],
  perilData: { [code: string]: TaskPeril }
) => {
  return events?.map((event: Task) => {
    const mapped: Task &
      Partial<{
        draggable: boolean;
        resizable: boolean;
        clickable: boolean;
        preamble: string;
        postamble: string;
        locationData?: {
          locationType: string;
          geoJson: {
            type: string;
            geometry: {
              type: string;
              coordinates: number[];
            };
            properties: {
              postalCode: string;
            };
          };
        };
      }> = event;

    mapped.draggable = false;
    // items are not resizable at the moment due to limitations
    mapped.resizable = false;
    if (mapped.type === 'no-location') {
      mapped.clickable = false;
    }

    mapped.source = 'scheduler';
    // if type is event, and was generated from scheduler then it should be able to be dragged
    const isTask = EVENT_TYPE.includes(mapped.type);

    if (isTask) {
      mapped.draggable = true;
    }

    if (mapped.type?.toLowerCase() !== 'event' && mapped.taskReferenceId) {
      mapped.id = `${mapped.taskReferenceId}`;
    }

    if (mapped.isRescheduleAllowed === true) {
      mapped.draggable = true;
    } else {
      mapped.draggable = false;
    }

    const preamble =
      event?.driveStartTime && event.startDate
        ? differenceInMinutes(
            new Date(event.startDate),
            new Date(event.driveStartTime)
          )
        : 0;

    const postamble =
      event?.homeArrivalTime && event.endDate
        ? differenceInMinutes(
            new Date(event.homeArrivalTime),
            new Date(event.endDate)
          )
        : 0;

    const eventMapped = {
      ...mapped,
      ignoreResourceCalendar: true,
      name: mapEventName(event),
      eventColor: mapColor(event, perilData),
      iconCls: mapIcons(event, perilData),
      targetQueue: event.targetQueue,
      isTask,
      postamble: '',
      preamble: '',
      postalCode: '',
    };

    if (preamble > 0) {
      eventMapped.preamble = `${preamble}min`;
    }

    if (postamble > 0) {
      eventMapped.postamble = `${postamble}min`;
    }

    if (mapped.locationData) {
      eventMapped.postalCode =
        mapped?.locationData?.geoJson?.properties?.postalCode || '';
    }

    return eventMapped;
  });
};

const getData = (type: 'events' | 'calendars' | 'resources', pages: any) => {
  return (
    pages
      ?.map(
        (page: {
          data: {
            calendars: Calendar[];
            resources: Resource[];
            events: Task[];
          };
        }) => page?.data?.[type] || []
      )
      .flat() || []
  );
};

const scheduleCacheTimeMs = 30 * 1000;

export const useGetScheduleData = (
  perils: { [code: string]: TaskPeril },
  filters: TaskListFilterGroup,
  view: 'list' | 'scheduler',
  enabled = true
) => {
  const scheduleData = usePaginatedQuery<SuperApiResponse>(
    ['schedule-data', filters],
    (token) => getScheduleData(filters, token),
    {
      enabled: view === 'scheduler' && enabled,
      staleTime: scheduleCacheTimeMs,
    }
  );

  const pages = scheduleData?.pages;

  return {
    ...scheduleData,
    data: {
      events: mapEvents(getData('events', pages), perils),
      calendars: getData('calendars', pages),
      resources: getData('resources', pages),
    },
  };
};
